/**
 * Pure decode/slice math for the modal volumetric pressure sweep
 * (`ModalVolumeSweepResponse`) — turns the backend's base64-packed
 * F·nx·ny·nz cube into a single frequency+height 2D grid slice, the input
 * `computeSplUniformityGeometry` (`@roomtreatment/charts`) needs for the SPL
 * Uniformity chart.
 *
 * Ported from `web/src/components/room/charts/sliceVolumetricSweep.ts`
 * (indexing math unchanged) — that file stays untouched, since this
 * workstream is not allowed to modify anything under `web/src`, so this is
 * a duplicate rather than a shared import until a later pass repoints web
 * at this copy. The base64 decode below is hand-rolled instead of calling
 * the DOM/RN `atob` global specifically so this module behaves identically
 * on web, Node (vitest) and React Native without depending on an
 * environment-provided global that isn't guaranteed present everywhere
 * (unlike a browser `<canvas>`, `atob` was only recently added to React
 * Native's JS globals and this module has no reason to require it).
 */
import type { ModalVolumeSweepMeta, ModalVolumeSweepResponse, VolumeSweepRefResponse } from '@roomtreatment/core';

const BASE64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

const BASE64_INDEX: Int16Array = (() => {
  const map = new Int16Array(128).fill(-1);
  for (let i = 0; i < BASE64_ALPHABET.length; i++) {
    map[BASE64_ALPHABET.charCodeAt(i)] = i;
  }
  return map;
})();

function base64ToBytes(b64: string): Uint8Array {
  let bitBuffer = 0;
  let bitCount = 0;
  const out: number[] = [];
  for (let i = 0; i < b64.length; i++) {
    const code = b64.charCodeAt(i);
    const val = code < 128 ? BASE64_INDEX[code] : -1;
    if (val === undefined || val < 0) continue; // skip '=' padding and whitespace
    bitBuffer = (bitBuffer << 6) | val;
    bitCount += 6;
    if (bitCount >= 8) {
      bitCount -= 8;
      out.push((bitBuffer >> bitCount) & 0xff);
    }
  }
  return Uint8Array.from(out);
}

export interface VolumetricSlice {
  nx: number;
  nz: number;
  /** Relative pressure magnitude, row-major: values[ix * nz + iz]. */
  values: number[];
  /** Air mask, same indexing as `values`. */
  inside: boolean[];
  roomWidthM: number;
  roomDepthM: number;
  /** The sweep's actual frequency this slice came from (nearest match to
   *  the requested target — the sweep is sampled at a coarse df). */
  frequencyHz: number;
}

export interface DecodedVolumetricSweep {
  field: Uint8Array;
  inside: Uint8Array;
}

/** Base64-decode the sweep's field/inside cubes once. This is the expensive
 *  part (proportional to F·nx·ny·nz bytes) — memoize it on the
 *  ModalVolumeSweepResponse reference, NOT on the frequency being viewed.
 *  Pair with `sliceDecodedVolumetricSweep` for the cheap per-frequency/
 *  height slice, which is safe to re-run on every scrubber tick. */
export function decodeVolumetricSweep(resp: ModalVolumeSweepResponse): DecodedVolumetricSweep {
  return {
    field: base64ToBytes(resp.field_b64),
    inside: base64ToBytes(resp.inside_b64),
  };
}

/** Slice an ALREADY-DECODED sweep (see `decodeVolumetricSweep`) at the
 *  frequency nearest `targetHz` and the voxel row nearest `heightM` (admin
 *  Y-up height, metres). O(nx·nz) — cheap, call this on every frequency
 *  change. Returns null if the sweep is empty or its grid has a zero
 *  dimension. */
export function sliceDecodedVolumetricSweep(
  decoded: DecodedVolumetricSweep,
  resp: ModalVolumeSweepMeta,
  targetHz: number,
  heightM: number,
): VolumetricSlice | null {
  if (!resp.freqs.length) return null;
  const [nx, ny, nz] = resp.grid.dims;
  if (!nx || !ny || !nz) return null;

  let fIdx = 0;
  let bestD = Infinity;
  resp.freqs.forEach((f, i) => {
    const d = Math.abs(f - targetHz);
    if (d < bestD) {
      bestD = d;
      fIdx = i;
    }
  });

  const originY = resp.grid.origin[1];
  const hY = resp.grid.h[1];
  const iy = Math.max(0, Math.min(ny - 1, Math.round((heightM - originY) / hY)));

  const { field, inside } = decoded;
  const vmax = resp.vmax_per_freq[fIdx] ?? 0;
  const sliceVoxels = nx * ny * nz;
  const rowStride = ny * nz;

  const values: number[] = new Array(nx * nz);
  const insideMask: boolean[] = new Array(nx * nz);
  for (let ix = 0; ix < nx; ix++) {
    for (let iz = 0; iz < nz; iz++) {
      // `inside_b64` has no frequency axis (it's pure geometry), so it's
      // indexed by voxelIdx alone; `field_b64` additionally offsets by the
      // chosen frequency slice.
      const voxelIdx = ix * rowStride + iy * nz + iz;
      const globalIdx = fIdx * sliceVoxels + voxelIdx;
      const byte = field[globalIdx] ?? 0;
      const out = ix * nz + iz;
      values[out] = (byte / 255) * vmax;
      insideMask[out] = (inside[voxelIdx] ?? 0) > 0;
    }
  }

  return {
    nx,
    nz,
    values,
    inside: insideMask,
    roomWidthM: nx * resp.grid.h[0],
    roomDepthM: nz * resp.grid.h[2],
    frequencyHz: resp.freqs[fIdx]!,
  };
}

/** Decode + slice in one call. Prefer `decodeVolumetricSweep` +
 *  `sliceDecodedVolumetricSweep` when slicing the SAME sweep repeatedly
 *  (e.g. a frequency scrubber) — this convenience wrapper re-decodes the
 *  full cube on every call. */
export function sliceVolumetricSweep(
  resp: ModalVolumeSweepResponse,
  targetHz: number,
  heightM: number,
): VolumetricSlice | null {
  return sliceDecodedVolumetricSweep(decodeVolumetricSweep(resp), resp, targetHz, heightM);
}

export interface VolumetricBandAggregate {
  nx: number;
  nz: number;
  /** Row-major, values[ix * nz + iz] — same indexing as `VolumetricSlice`. */
  values: number[];
  inside: boolean[];
}

/** Reduces an ALREADY-DECODED sweep across a frequency BAND (not a single
 *  slice — see `sliceDecodedVolumetricSweep` for that) into one 2D
 *  room-plan grid, for the Sweet Spot Map chart's Mode A ("sweep listening
 *  position"). Ported from
 *  `web/src/components/room/charts/sliceVolumetricSweep.ts` (same reasoning
 *  as this file's header doc — a duplicate, not a shared import, until web
 *  is repointed at this package).
 *
 *  `metric`:
 *  - `'variance'`: per-cell variance of the band's samples — lower is more
 *    uniform. The chart's shared heatmap convention treats a HIGHER
 *    `values` entry as louder/better, so callers invert this (e.g.
 *    `1 / (1 + variance)`) before handing it to `computeSplUniformityGeometry`.
 *  - `'worstCaseDb'`: the minimum (quietest) pressure sample in the band —
 *    higher is better, no inversion needed. Despite the name this is the
 *    same relative linear pressure magnitude `sliceDecodedVolumetricSweep`
 *    returns, not an actual decibel value; the sweep never stores one.
 *
 *  If the [freqLoHz, freqHiHz] band contains none of the sweep's sampled
 *  frequencies, falls back to the single freq nearest the band's midpoint
 *  so the result is never empty. Returns an all-zero grid if the sweep or
 *  grid is degenerate (no freqs, or a zero grid dimension). */
export function aggregateVolumetricSweepBand(
  decoded: DecodedVolumetricSweep,
  resp: ModalVolumeSweepMeta,
  freqLoHz: number,
  freqHiHz: number,
  heightM: number,
  metric: 'variance' | 'worstCaseDb',
): VolumetricBandAggregate {
  const [nx, ny, nz] = resp.grid.dims;
  if (!resp.freqs.length || !nx || !ny || !nz) {
    return { nx: nx ?? 0, nz: nz ?? 0, values: [], inside: [] };
  }

  const lo = Math.min(freqLoHz, freqHiHz);
  const hi = Math.max(freqLoHz, freqHiHz);
  let freqIdxs = resp.freqs
    .map((f, i) => ({ f, i }))
    .filter(({ f }) => f >= lo && f <= hi)
    .map(({ i }) => i);

  if (!freqIdxs.length) {
    // Band misses every sample — fall back to the single freq nearest the
    // band's midpoint so the result is never empty.
    const mid = (lo + hi) / 2;
    let bestI = 0;
    let bestD = Infinity;
    resp.freqs.forEach((f, i) => {
      const d = Math.abs(f - mid);
      if (d < bestD) {
        bestD = d;
        bestI = i;
      }
    });
    freqIdxs = [bestI];
  }

  const originY = resp.grid.origin[1];
  const hY = resp.grid.h[1];
  const iy = Math.max(0, Math.min(ny - 1, Math.round((heightM - originY) / hY)));

  const { field, inside } = decoded;
  const rowStride = ny * nz;
  const sliceVoxels = nx * ny * nz;

  const values: number[] = new Array(nx * nz).fill(0);
  const insideMask: boolean[] = new Array(nx * nz).fill(false);

  for (let ix = 0; ix < nx; ix++) {
    for (let iz = 0; iz < nz; iz++) {
      const voxelIdx = ix * rowStride + iy * nz + iz;
      const cellIdx = ix * nz + iz;
      const isInside = (inside[voxelIdx] ?? 0) > 0;
      insideMask[cellIdx] = isInside;
      if (!isInside) continue;

      const samples = freqIdxs.map((fIdx) => {
        const byte = field[fIdx * sliceVoxels + voxelIdx] ?? 0;
        const vmax = resp.vmax_per_freq[fIdx] ?? 0;
        return (byte / 255) * vmax;
      });

      if (metric === 'worstCaseDb') {
        values[cellIdx] = Math.min(...samples);
      } else {
        const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
        values[cellIdx] = samples.reduce((a, b) => a + (b - mean) ** 2, 0) / samples.length;
      }
    }
  }

  return { nx, nz, values, inside: insideMask };
}

/** Injected artifact fetcher — same signature as
 *  `@roomtreatment/api-client`'s `AdminApi.fetchArtifact`, duplicated here
 *  (rather than a dependency on `api-client`) so this package stays a pure
 *  math library with no fetch/DOM assumptions; the caller supplies whatever
 *  fetcher it already has. */
export type FetchArtifactFn = (sha: string, signal?: AbortSignal) => Promise<Uint8Array>;

/** Job pipeline W2 (2026-09-06), §3.2/3.5: `analyze_scene`'s volume/velocity
 *  layers arrive as artifact references (`{ field: ArtifactRef, inside:
 *  ArtifactRef }`) instead of the legacy inline `field_b64`/`inside_b64`.
 *  This is the ONE adapter that turns either shape into the
 *  `DecodedVolumetricSweep` bytes `sliceDecodedVolumetricSweep` /
 *  `aggregateVolumetricSweepBand` already accept — callers must not
 *  hand-roll a second decode path for the ref shape. Legacy inline
 *  responses resolve synchronously (still returned as a Promise for a
 *  uniform call site); ref-form responses await `fetchArtifact` for both
 *  cubes in parallel. */
export function resolveVolumeBytes(
  resp: ModalVolumeSweepResponse | VolumeSweepRefResponse,
  fetchArtifact: FetchArtifactFn,
): Promise<DecodedVolumetricSweep> {
  if ('field_b64' in resp) {
    return Promise.resolve(decodeVolumetricSweep(resp));
  }
  return Promise.all([
    fetchArtifact(resp.field.artifact),
    fetchArtifact(resp.inside.artifact),
  ]).then(([field, inside]) => ({ field, inside }));
}
